import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MenuItem } from 'primeng/api';
import { combineLatest, combineLatest as observableCombineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { AssetIndexTypes } from '../../../../shared/enums/asset-index-types.enum';
import { FIELD_TYPES } from '../../../../shared/fields/field-types';
import { STANDARD_FIELDS } from '../../../../shared/fields/standard-fields';
import { Entities } from '../../../../shared/types';
import { ColumnFilterOptions } from '../../../../shared/types/column-filter-options';
import { ListOptions } from '../../../../shared/types/list-options';
import * as fromRoot from '../../core/store';
import * as assetActions from '../../core/store/assets/asset.actions';
import { AssetTypes } from '../../core/store/assets/asset.model';
import * as fieldActions from '../../core/store/fields/field.actions';
import * as franchiseAssetIndexActions from '../../core/store/franchise-asset-index/franchise-asset-index.actions';
import {
    Asset,
    Character,
    CustomStudioFieldName,
    Department,
    Disposition,
    Division,
    Field,
    Franchise,
    Group,
    Location,
    Project,
    ProjectCurrency,
    Set as SetModel,
    User
} from '../../core/store/models';
import {
    assetSelectors,
    characterSelectors,
    divisionSelectors,
    fieldSelectors,
    groupSelectors,
    locationSelectors,
    projectCurrencySelectors,
    setSelectors,
    navSelectors
} from '../../core/store/selectors';
import * as navActions from '../../core/store/nav/nav.actions';
import * as sidenavFilterActions from '../../core/store/sidenav-filter/sidenav-filter.actions';
import { ActionStatus, SortObject, TableFilterObject, TableTypes } from '../../core/store/types';
import { buildStateKey, updateTableStateFiltersWithSidenav } from '../../core/store/utils';
import { FilterOption } from '../../layout/sidenav-filter/sidenav-filter.component';
import { PageType } from '../../layout/sidenav-filter/sidenav-filter.component';
import { SelectType, SidenavFilterService } from '../../layout/sidenav-filter/sidenav-filter.service';
import { setSidenavFilterDepartments, validateTableState, reListingAssets } from '../../shared/components/asset-data-table/asset-data-table.helpers';
import { AssetDataService } from '../../shared/data-services/asset-data.service';
import { AssetModalDataService } from '../../shared/data-services/asset-modal-data.service';
import { PDropdownOption } from '../../shared/interfaces/p-dropdown-options';
import { ColumnFilterService } from '../../shared/services/column-filter.service';
import { TransactionFormService } from '../../transactions/services/transaction-form.service';
import { ASSET_MODAL_TYPE } from '../asset-modal/asset-modal.component';
import * as pageSelectors from './franchise-asset-index.selectors';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { LIMIT } from '../../../../shared/constants/asset-limit.constant';
import * as layoutActions from '../../core/store/layout/layout.actions';
import { CSV_EXPORT_MESSAGE } from '../../shared/constants/constants';

export const FranchiseAssetViews = { assets: 'Assets', review: 'For Review' };

@Component({
    selector: 'ah-franchise-asset-index',
    templateUrl: './franchise-asset-index.component.html',
    styleUrls: ['./franchise-asset-index.component.scss', '../../sass/shared/column-selector.scss'],
    providers: [SidenavFilterService, TransactionFormService],
    standalone: false
})
export class FranchiseAssetIndexComponent implements OnInit, OnDestroy {
    activeTab: MenuItem;
    assetEntities: Entities<Asset>;
    tabs: MenuItem[];
    departments$: Observable<Department[]>;
    dispositions$: Observable<Disposition[]>;
    bulkModalLocations$: Observable<Location[]>;
    projectList$: Observable<Project[]>;
    selectedProjectIdsList: number[] = [];
    sosDepartments$: Observable<Department[]>;
    currentStudioId: number;

    bulkModalCharacters$: Observable<Character[]>;
    bulkModalSets$: Observable<SetModel[]>;

    allFieldOptions: PDropdownOption[];
    allGroups$: Observable<Group[]>;
    assetIndexTypes = AssetIndexTypes;
    assets: Asset[] = [];
    assetCountTotal = 0;
    assetCountForReviewTotal = 0;
    assetTypes = AssetTypes;
    assetTypesShown: AssetTypes;
    assetValidLookup: { [assetId: number]: boolean } = {};
    canEdit: boolean;
    columnFilters: TableFilterObject = {};
    columnFiltersForReview: TableFilterObject;
    currentView = AssetIndexTypes.Default;
    customFieldNames: CustomStudioFieldName;
    defaultStateKey: string;
    moveToDivisions: Division[];
    fields: Field[] = [];
    franchise: Franchise;
    franchiseGroups$: Observable<Group[]>;
    notFranchiseGroups$: Observable<Group[]>;
    filters: TableFilterObject = {};
    filtersForReview: TableFilterObject = {};
    forReviewAssets$: Observable<Asset[]>;
    forReviewNotification = false;
    forReviewNotObs: Asset[] = [];
    forReviewStateKey: string;
    hasBarcodeField = false;
    projectCurrencies$: Observable<ProjectCurrency[]>;
    options: ListOptions;
    optionsForReview: ListOptions;
    selectOptions$: Observable<ColumnFilterOptions>;
    selectOptionsForReview$: Observable<ColumnFilterOptions>;
    selectedFields: Field[] = [];
    selectedFieldOptions: number[];
    sub: Subscription;
    selectedAssets: Set<number> = new Set();
    selectedProjectCurrencyId: number = null;
    storedSelectedPC: { [projectId: string]: number } = {};
    tableType = TableTypes.FranchiseTable;
    projectId: number;
    updateCharSetOptions = false;
    user: User;
    views = FranchiseAssetViews;
    isForReviewChanged = false;
    allowDept: boolean;
    allowProject: boolean;
    projectList: Project[] = [];
    departmentList: number[] = [];
    selectedDeptIdsList: number[] = [];
    recordIndex? = 0;
    pageSize? = 50;
    clearCache = false;
    clearCacheForReview = false;
    recordIndexForReview? = 0;
    pageSizeForReview? = 50;
    assetLimit: number = LIMIT;
    allAssetsSelected: boolean;
    allAssetsSelectedForReview: boolean;

    constructor(private route: ActivatedRoute, private router: Router, private store: Store<fromRoot.State>, public assetDataService: AssetDataService, public assetModalDataService: AssetModalDataService, public dialog: MatDialog, public sidenav: SidenavFilterService, private transactionFormService: TransactionFormService, public columnFilterService: ColumnFilterService, private localStorageService: LocalStorageService) { }

    ngOnInit() {
        this.tabs = [
            { label: this.views.assets, command: this.changeView.bind(this) },
            { label: this.views.review, command: this.changeView.bind(this) }
        ];
        this.store.dispatch(new franchiseAssetIndexActions.SelectTabAction(0));
        const { selectedFranchise, user, customFieldNames } = this.route.parent.snapshot.data;
        this.franchise = selectedFranchise;
        this.user = user;
        this.customFieldNames = customFieldNames;
        this.currentStudioId = this.franchise.studio_id;
        this.canEdit = this.user.permission.canEditInFranchise(this.franchise.id, this.franchise.studio_id);
        this.sidenav.width = 200;
        this.defaultStateKey = buildStateKey({ franchiseId: this.franchise.id, indexType: AssetIndexTypes.Default });
        this.forReviewStateKey = buildStateKey({ franchiseId: this.franchise.id, indexType: AssetIndexTypes.ForReview });

        this.store.dispatch(new navActions.ListStatusAction({ studioId: this.user.current_studio_id, franchiseId: this.franchise.id }));
        this.store.dispatch(new assetActions.ListByFranchiseFiltersAction({ franchiseId: this.franchise.id, assetTypes: this.assetTypesShown }));
        this.store.dispatch(new navActions.ToggleAssetFiltersOptionAction());
        this.store.dispatch(new fieldActions.ListAssetSelectedAction());
        this.store.dispatch(new assetActions.SelectAssetsShown(AssetTypes.Franchise));
        this.store.dispatch(new franchiseAssetIndexActions.ListTableStateAction({ franchiseId: this.franchise.id, stateKey: this.defaultStateKey }));
        this.store.dispatch(new franchiseAssetIndexActions.ListReviewTableStateAction({ franchiseId: this.franchise.id, stateKey: this.forReviewStateKey }));

        this.departments$ = this.store.select(fromRoot.getDepartmentsForSelectedFranchise).pipe(filter(depts => !!depts.length));
        this.sosDepartments$ = this.store.select(fromRoot.getSosDepartmentsForSelectedFranchise).pipe(filter(depts => !!depts.length));
        this.dispositions$ = this.store.select(fromRoot.getDispositions).pipe(filter(dps => !!dps));
        this.allGroups$ = this.store.select(groupSelectors.getAllGroups);
        this.franchiseGroups$ = this.store.select(groupSelectors.getGroupsForSelectedFranchise);
        this.projectList$ = this.store.select(fromRoot.getSelectedFranchiseProjects).pipe(filter(prj => !!prj));
        this.projectCurrencies$ = this.store.select(projectCurrencySelectors.getForSelectedProjectOnAsset);
        this.bulkModalLocations$ = this.store.select(locationSelectors.getLocationsForSelectedFranchise).pipe(filter(locs => !!locs));
        this.notFranchiseGroups$ = this.store.select(groupSelectors.getGroupsNotInSelectedFranchise);

        this.bulkModalCharacters$ = this.store.select(characterSelectors.getAssetBulkModalCharactersForSelectedProject).pipe(filter(chars => !!chars));
        this.bulkModalSets$ = this.store.select(setSelectors.getAssetBulkModalSetsForSelectedProject).pipe(filter(sets => !!sets));

        this.selectOptions$ = this.store.select(pageSelectors.getColumnFilters);
        this.selectOptionsForReview$ = this.store.select(pageSelectors.getColumnFiltersForReview);
        const deptOptions$: Observable<FilterOption[]> = observableCombineLatest<any>(
            this.store.select(pageSelectors.getActiveTab),
            this.selectOptions$,
            this.selectOptionsForReview$
        ).pipe(
            map(([activeTab, options, forReviewOptions]) => {
                let deptOptions = [];

                if (activeTab === 1) {
                    deptOptions = forReviewOptions[STANDARD_FIELDS.department.canonical_name];
                } else {
                    deptOptions = options[STANDARD_FIELDS.department.canonical_name];
                }

                if (!deptOptions) {
                    return [];
                }

                this.departmentList = deptOptions.map(val => val.value);
                const selectedDeps = this.selectedDeptIdsList;
                let uniqueChars = this.departmentList.filter(val => selectedDeps.indexOf(val) === -1);
                this.allowDept = uniqueChars.length === 0 ? true : false;

                return deptOptions.map(option => ({
                    id: option.value,
                    name: option.label
                }));
            })
        );

        const projectPrefix = this.franchise.is_series ? this.customFieldNames.season_id : '';
        this.sidenav.filters = [{
            name: 'projects',
            entities: this.projectList$,
            entityPrefix: projectPrefix,
            selectedEntityIds: this.selectedProjectIdsList,
            title: this.franchise.name,
            type: SelectType.Multi,
            search: false
        }, {
            name: 'departments',
            entities: deptOptions$,
            selectedEntityIds: [],
            title: 'Departments',
            type: SelectType.Multi,
            search: false
        }];

        this.projectId = this.selectedProjectIdsList.length === 1 ? this.selectedProjectIdsList[0] : null;
        this.storedSelectedPC = JSON.parse(localStorage.getItem('selectedProjectCurrency'));
        this.storedSelectedPC = this.storedSelectedPC ? this.storedSelectedPC : {};

        const savedFilters = this.columnFilterService.getSavedColumnFilterValues(`franchise${this.franchise.id}AssetFilter`);
        if (savedFilters) {
            // call the action to set the filter
        }
        const savedForReviewFilters = this.columnFilterService.getSavedColumnFilterValuesForSecondTable(`franchise${this.franchise.id}ForReviewAssetFilter`);
        if (savedForReviewFilters) {
            // call the action to set the filter
        }

        this.assetModalDataService.dispatchData(null, this.franchise);

        // This feels pretty hacky, but it's here to get the stuff from
        // session storage into redux so some selectors that rely on the filters
        // work on page load
        this.filterTableColumns({
            type: AssetIndexTypes.Default,
            filters: {}
        });
        this.filterTableColumns({
            type: AssetIndexTypes.ForReview,
            filters: {}
        });
        const storedProjectIdsObject = JSON.parse(localStorage.getItem(`sidenav${PageType.Asset}Projects`));
        const storedProjectIds = storedProjectIdsObject && storedProjectIdsObject[this.franchise.id] ? storedProjectIdsObject[this.franchise.id] : [];
        if (storedProjectIds.length > 0) {
            this.projectList$.pipe(take(1)).subscribe((projects: Project[]) => {
                this.projectList = projects;
                for (const projectId of storedProjectIds) {
                    const project = projects.find(project => project.id === projectId);
                    if (project) {
                        this.store.dispatch(new sidenavFilterActions.UpdateProjectAction({ franchiseId: project.franchise_id, projectId: project.id, selectType: SelectType.Multi, pageType: PageType.Asset }));
                        this.selectEntity({
                            name: 'projects',
                            entity: project,
                            selectType: SelectType.Multi
                        });
                    }
                }
            })
        }
        this.setupSubscriptions();
        this.route.queryParams.subscribe(params => {
            if (params.focus === 'review') {
                this.store.select(pageSelectors.getReviewAssetCountForFranchise).subscribe((forReviewTotal) => {
                    this.assetCountForReviewTotal = forReviewTotal;
                });
                this.currentView = AssetIndexTypes.ForReview;
                const tabIndex = 1;
                this.store.dispatch(new franchiseAssetIndexActions.SelectTabAction(tabIndex));
            }
        });
    }

    ngOnDestroy() {
        this.store.dispatch(new navActions.ToggleAssetFiltersOptionAction());
        this.assetModalDataService.unsubscribe();
        this.sidenav.unsubscribe();
        if (this.sub) { this.sub.unsubscribe(); }
    }

    changeView(event) {
        this.currentView = event.item.label === this.views.assets ? AssetIndexTypes.Default : AssetIndexTypes.ForReview;
        const tabIndex = event.item.label === this.views.assets ? 0 : 1;
        this.store.dispatch(new franchiseAssetIndexActions.SelectTabAction(tabIndex));
    }

    openCreateDialog() {
        const disableOwnerChange = !this.user.permission.isFranchiseAdmin(this.franchise.id, this.franchise.studio_id) && !this.user.permission.hasDivisionPermissionForStudio(this.franchise.studio_id);

        let projectId = null;
        this.projectList$.pipe(take(1)).subscribe((projects: Project[]) => {
            if (projects.length === 1) {
                projectId = projects[0].id;
            }
            const asset = new Asset({
                studio_id: this.franchise.studio_id,
                franchise_id: this.franchise.id,
                project_id: projectId
            });
            this.assetModalDataService.openModal(this.dialog, ASSET_MODAL_TYPE.CREATE, this.user, this.tableType, asset, disableOwnerChange, this.customFieldNames);
        });
    }

    listAssetsAndFilters({ listType }) {
        const stateKey = listType === AssetIndexTypes.ForReview ? this.forReviewStateKey : this.defaultStateKey;
        const tableState = JSON.parse(localStorage.getItem(stateKey) ?? 'null');

        if (tableState) {
            if (this.currentView === AssetIndexTypes.ForReview) {
                this.recordIndexForReview = tableState.first;
                this.pageSizeForReview = tableState.rows;
            } else {
                this.recordIndex = tableState.first;
                this.pageSize = tableState.rows;
            }
        }

        this.store.dispatch(new assetActions.ListByFranchiseFiltersAction({ franchiseId: this.franchise.id, assetTypes: this.assetTypesShown }));
        if (listType === AssetIndexTypes.ForReview) {
            this.isForReviewChanged = true;
            this.recordIndexForReview = 0;
            this.clearCacheForReview = true;
            this.forReviewNotObs = [];
            this.store.dispatch(new navActions.ListStatusAction({ studioId: this.user.current_studio_id, franchiseId: this.franchise.id }));

            const forReviewOptions = this.allAssetsSelectedForReview
                ? { ...this.optionsForReview, listGroups: true }
                : { ...this.optionsForReview, listGroups: true, recordIndex: this.recordIndexForReview, pageSize: this.pageSizeForReview };

            this.store.dispatch(new assetActions.ListByFranchiseAction({
                franchiseId: this.franchise.id,
                options: forReviewOptions
            }));
        } else {
            const assetsOptions = this.allAssetsSelected
                ? { ...this.options, listGroups: true }
                : { ...this.options, recordIndex: this.recordIndex, pageSize: this.pageSize, listGroups: true };

            this.store.dispatch(new assetActions.ListByFranchiseAction({
                franchiseId: this.franchise.id,
                options: assetsOptions
            }));
        }
    }

    selectProject(selectedData: { name: string, allowProject: boolean }) {
        if (selectedData.name === 'Departments') {
            let deptFilters = {
                matchMode: this.columnFilterService.getFilterMatchMode(STANDARD_FIELDS.department.type),
                value: selectedData.allowProject ? this.departmentList : []
            };
            const newFilters = {
                [STANDARD_FIELDS.department.canonical_name]: deptFilters
            };
            if (this.currentView === AssetIndexTypes.ForReview) {
                this.store.dispatch(new franchiseAssetIndexActions.FilterFranchiseForReviewListAction({ franchiseId: this.franchise.id, filter: newFilters }));
            } else {
                this.store.dispatch(new franchiseAssetIndexActions.FilterFranchiseListAction({ franchiseId: this.franchise.id, filter: newFilters }));
            }
            this.allowDept = selectedData.allowProject ? true : false;
        } else {
            this.sidenav.selectMultipleEntity(this.projectList, SelectType.Multi, PageType.Asset, this.franchise, selectedData.allowProject)
        }
    }

    selectEntity(selectData: { name: string, entity: any, selectType: SelectType }) {
        if (selectData.name === 'departments') {
            const id = selectData.entity.id;
            const filters = this.currentView === AssetIndexTypes.ForReview ? this.filtersForReview : this.filters;
            let deptFilters = { ...filters[STANDARD_FIELDS.department.canonical_name] };
            if (!deptFilters || !deptFilters.value) {
                deptFilters = {
                    matchMode: this.columnFilterService.getFilterMatchMode(STANDARD_FIELDS.department.type),
                    value: [id]
                };
            } else {
                const i = deptFilters.value.indexOf(id);
                if (i > -1) {
                    deptFilters.value = [...deptFilters.value];
                    deptFilters.value.splice(i, 1);
                } else {
                    deptFilters.value = [...deptFilters.value, id];
                }
            }
            const newFilters = {
                ...filters,
                [STANDARD_FIELDS.department.canonical_name]: deptFilters
            };
            if (this.currentView === AssetIndexTypes.ForReview) {
                updateTableStateFiltersWithSidenav(this.forReviewStateKey, STANDARD_FIELDS.department.canonical_name, id);
                this.store.dispatch(new franchiseAssetIndexActions.FilterFranchiseForReviewListAction({ franchiseId: this.franchise.id, filter: newFilters }));
            } else {
                updateTableStateFiltersWithSidenav(this.defaultStateKey, STANDARD_FIELDS.department.canonical_name, id);
                this.store.dispatch(new franchiseAssetIndexActions.FilterFranchiseListAction({ franchiseId: this.franchise.id, filter: newFilters }));
            }
        } else {
            this.sidenav.selectEntity(selectData.entity, selectData.selectType, PageType.Asset, this.franchise);
        }
    }

    selectProjectCurrency(projectCurrencyId: number) {
        if (this.projectId) {
            this.storedSelectedPC[this.projectId] = projectCurrencyId;
            this.localStorageService.updateSettings('selectedProjectCurrency', JSON.stringify(this.storedSelectedPC));
            this.selectedProjectCurrencyId = projectCurrencyId;
        }
    }

    filterTableColumns(filterData: { type: AssetIndexTypes, filters: TableFilterObject, reset?: boolean }) {
        if (filterData.type === AssetIndexTypes.ForReview) {
            this.store.dispatch(new franchiseAssetIndexActions.FilterFranchiseForReviewListAction({ franchiseId: this.franchise.id, filter: filterData.filters, reset: filterData.reset }));
        } else {
            this.store.dispatch(new franchiseAssetIndexActions.FilterFranchiseListAction({ franchiseId: this.franchise.id, filter: filterData.filters, reset: filterData.reset }));
        }
        if (filterData.reset) {
            const stateKey = filterData.type === AssetIndexTypes.ForReview ? this.forReviewStateKey : this.defaultStateKey;
            this.localStorageService.updateSettings(stateKey, JSON.stringify({ filters: filterData.filters }));
        }
    }

    sortTable(sortData: { type: AssetIndexTypes, sort: SortObject }) {
        if (sortData.type === AssetIndexTypes.ForReview) {
            this.store.dispatch(new franchiseAssetIndexActions.SortFranchiseForReviewAssetsAction({ franchiseId: this.franchise.id, sort: sortData.sort }));
        } else {
            this.store.dispatch(new franchiseAssetIndexActions.SortFranchiseAssetsAction({ franchiseId: this.franchise.id, sort: sortData.sort }));
        }
    }

    viewTrash() {
        this.router.navigate(['franchise', this.franchise.id, 'trash']);
    }


    getForReviewAssets(pageEvent: any) {
        let listOptions: ListOptions;

        if (pageEvent && pageEvent.recordIndex != null && pageEvent.pageSize != null) {
            const tableState = JSON.parse(localStorage.getItem(this.forReviewStateKey) ?? 'null');
            if (tableState) {
                if (pageEvent.pageSize !== tableState.rows) {
                    this.clearCacheForReview = true;
                }
            }

            if (pageEvent.recordIndex === null) {
                pageEvent.recordIndex = 0;
            }

            if (this.allAssetsSelectedForReview) {
                listOptions = {
                    ...this.optionsForReview,
                    listGroups: true,
                    projectIds: this.selectedProjectIdsList
                };
            } else {
                // Only set clearCacheForReview if recordIndex changed (new page)
                if (pageEvent.recordIndex !== this.recordIndexForReview || pageEvent.pageSize !== this.pageSizeForReview) {
                    this.clearCacheForReview = true;
                }
                this.recordIndexForReview = pageEvent.recordIndex;
                this.pageSizeForReview = pageEvent.pageSize;

                listOptions = {
                    ...this.optionsForReview,
                    recordIndex: pageEvent.recordIndex,
                    pageSize: pageEvent.pageSize,
                    listGroups: true,
                    projectIds: this.selectedProjectIdsList
                };
            }
        } else {
            // Full reset — null out pagination so subscription uses full array path
            this.recordIndexForReview = null;
            this.pageSizeForReview = null;
            this.clearCacheForReview = true;

            listOptions = {
                ...this.optionsForReview,
                listGroups: true,
                projectIds: this.selectedProjectIdsList
            };

            if (this.allAssetsSelectedForReview) {
                delete listOptions.recordIndex;
                delete listOptions.pageSize;
            }
        }

        listOptions.forReview = true;
        this.store.dispatch(new assetActions.ListByFranchiseAction({
            franchiseId: this.franchise.id,
            options: listOptions,
        }));
    }

    private setupSubscriptions() {
        this.sub = new Subscription();

        const getAssetsAndFiltersSub = combineLatest([
            this.departments$,
            this.store.select(pageSelectors.getFields),
            this.store.select(assetSelectors.getCurrentFranchiseAssets),
            this.store.select(assetSelectors.getCurrentReviewAssets),
            this.store.select(fromRoot.getSidenavAssetProjectIds),
            this.store.select(fromRoot.getSelectedFranchiseProjects),
            this.store.select(assetSelectors.getAssetCountForCurrentFranchise),
            this.store.select(pageSelectors.getReviewAssetCountForFranchise),
            this.store.select(fromRoot.getAssetLoading),
            this.store.select(pageSelectors.getActiveTab)  // ✅ Keep in combineLatest
        ]).pipe(
            filter(([departments, fields, _assets, _forReviewAssets, selectedProjIds, allProjIds, _assetTotal, _forReviewTotal, loading, _activeTab]) => {
                return !!departments && !!fields && !!selectedProjIds && !!allProjIds && loading === ActionStatus.Complete;
            })
        ).subscribe(([departments, fields, assets, forReviewAssets, selectedProjIds, allProjIds, assetTotal, forReviewTotal, _loading, activeTab]: [Department[], Field[], Asset[], Asset[], number[], Project[], number, number, ActionStatus, number]) => {

            // ✅ Bug 2 Fix: Process BOTH tabs regardless — use currentView instead of activeTab
            // activeTab from store can lag behind actual view during pagination

            // Handle regular assets (Assets tab)
            // ✅ Process when currentView is Default OR activeTab is 0
            if (this.currentView === AssetIndexTypes.Default) {
                let tempAssets: Asset[];
                if (assetTotal > 0) {
                    if (!this.clearCache) {
                        tempAssets = this.assets;
                    } else {
                        tempAssets = new Array<Asset>(assetTotal);
                        this.clearCache = false;
                    }

                    if (this.recordIndex != null && this.pageSize != null) {
                        for (let i = this.recordIndex; i < this.pageSize + this.recordIndex; i++) {
                            if (assets[i - this.recordIndex]) {
                                tempAssets[i] = assets[i - this.recordIndex];
                            }
                        }
                    } else {
                        tempAssets = assets;
                    }

                    this.assetCountTotal = assetTotal;
                    this.assets = tempAssets;
                } else {
                    this.assets = [];
                    this.assetCountTotal = 0;
                }
            }

            // ✅ ForReview tab virtual scroll
            if (this.currentView === AssetIndexTypes.ForReview) {
                if (forReviewTotal > 0) {
                    let tempForReviewAssets: Asset[];

                    if (this.clearCacheForReview || this.forReviewNotObs.length !== forReviewTotal) {
                        // Always create a fresh array when cache is cleared or total changed
                        tempForReviewAssets = new Array<Asset>(forReviewTotal);
                        this.clearCacheForReview = false;
                    } else {
                        // Reuse existing array to preserve already-loaded pages
                        tempForReviewAssets = [...this.forReviewNotObs];
                    }

                    if (this.recordIndexForReview != null && this.pageSizeForReview != null) {
                        for (let i = this.recordIndexForReview; i < this.pageSizeForReview + this.recordIndexForReview; i++) {
                            const sourceIndex = i - this.recordIndexForReview;
                            if (forReviewAssets[sourceIndex]) {
                                tempForReviewAssets[i] = forReviewAssets[sourceIndex];
                            }
                        }
                    } else {
                        // No pagination — replace entirely
                        tempForReviewAssets = forReviewAssets;
                    }

                    this.assetCountForReviewTotal = forReviewTotal;
                    this.forReviewNotObs = tempForReviewAssets;

                    this.forReviewNotObs.forEach(asset => {
                        if (!asset) return;
                        const dept = departments.find((department) => department.id === asset.department_id);
                        if (dept) {
                            const fieldsWithDeptFields = this.transactionFormService.buildWithDeptFields(dept, fields);
                            this.assetValidLookup[asset.id] = !fieldsWithDeptFields.some(field => {
                                return field.departmentField.required && field.field && !asset[field.field.canonical_name];
                            });
                        }
                    });
                } else {
                    this.forReviewNotObs = [];
                    this.assetCountForReviewTotal = 0;
                    this.clearCacheForReview = false;
                }
            }

            // ... existing code for project selection
            this.selectedProjectIdsList = selectedProjIds;
            const projectIds = this.projectList.length > 0 ? this.projectList.map(val => val.id) : [];
            if (this.selectedProjectIdsList.length > 0) {
                let uniqueChars = projectIds.filter(val => selectedProjIds.indexOf(val) === -1);
                this.allowProject = uniqueChars.length === 0 ? true : false;
            }
            this.projectId = this.selectedProjectIdsList.length === 1 ? this.selectedProjectIdsList[0] : null;
            this.selectedProjectCurrencyId = this.projectId ? this.selectedProjectCurrencyId : null;

            if (this.sidenav.filters[0]) {
                this.sidenav.filters[0].selectedEntityIds = this.selectedProjectIdsList;
                if (this.sidenav.filters[0].selectedEntityIds.length === 0 && !selectedProjIds.length) {
                    this.projectList$.pipe(take(1)).subscribe(projects => {
                        this.projectList = projects;
                        const sortedProjects = [...projects].sort((a, b) => a.id < b.id ? -1 : 1);
                        this.selectEntity({
                            name: 'projects',
                            entity: sortedProjects[0],
                            selectType: SelectType.Multi
                        });
                    });
                }
            }

            this.updateCharSetOptions = selectedProjIds.length === 1 || allProjIds.length === 1 ? true : false;
        });

        let previousFranchiseListOptions = {};
        const getFranchiseListSub = observableCombineLatest<any>(
            this.store.select(pageSelectors.getActiveTab),
            this.store.select(pageSelectors.getFranchiseAssetSortForSelectedFranchise),
            this.store.select(pageSelectors.getFranchiseAssetFilterForSelectedFranchise),
            this.store.select(fromRoot.getSidenavAssetProjectIds),
            this.store.select(fromRoot.getAssetsShown),
            this.store.select(pageSelectors.getSelectedFields),
            this.selectOptions$,
            this.store.select(fromRoot.getAssetLimit)
        ).pipe(filter(([activeTab, franchiseSort, _franchiseFilter, _projectIds, _assetsShown, f, _options, _limit]: [number, any, any, number[], AssetTypes, Field[], ColumnFilterOptions, number]) => {
            return !!f && !!f.length && activeTab === 0 && !!franchiseSort;
        })).subscribe(([activeTab, franchiseSort, franchiseFilter, projectIds, assetsShown, fields, selectOptions, assetLimit]: [number, any, any, number[], AssetTypes, Field[], ColumnFilterOptions, number]) => {
            const deptFilters = franchiseFilter ? franchiseFilter[STANDARD_FIELDS.department.canonical_name] : null;
            this.sidenav.filters[1] = setSidenavFilterDepartments(deptFilters, AssetIndexTypes.Default, this.currentView, this.sidenav.filters[1]);
            this.selectedDeptIdsList = this.sidenav.filters[1].selectedEntityIds;
            const { filters, syncWithRedux } = this.localStorageService.validateFiltersAndSelectOptions(franchiseFilter, selectOptions, this.defaultStateKey);
            this.filters = { ...filters };
            fields = fields.map(field => ({ ...field, departmentFields: [] })) as Field[];
            this.options = {
                sort: franchiseSort,
                filter: filters,
                projectIds,
                assetTypes: assetsShown,
                selectedFields: fields,
                forReview: false,
                limit: assetLimit
            };
            this.assetLimit = assetLimit;
            if (syncWithRedux) {
                this.store.dispatch(new franchiseAssetIndexActions.FilterFranchiseListAction({ franchiseId: this.franchise.id, filter: this.filters }));
            }

            if (assetsShown === AssetTypes.Historical) {
                if (!this.options.historicalSearch) {
                    this.options.historicalSearch = {};
                }
                this.options.historicalSearch[STANDARD_FIELDS.franchise.canonical_name] = this.franchise.id;
            }

            this.activeTab = this.tabs[activeTab];
            const newFranchiseListOptions = this.options;
            if (reListingAssets(previousFranchiseListOptions, newFranchiseListOptions) || this.isForReviewChanged) {
                this.isForReviewChanged = false;

                const tableState = JSON.parse(localStorage.getItem(this.defaultStateKey) ?? 'null');

                if (tableState) {
                    this.pageSize = (tableState.rows) ? tableState.rows : 50;
                    this.recordIndex = 0;

                    tableState.rows = this.pageSize;
                    tableState.first = this.recordIndex;
                }

                this.assets = [];
                this.store.dispatch(new assetActions.ListByFranchiseAction({
                    franchiseId: this.franchise.id,
                    options: { ...this.options, recordIndex: this.recordIndex, pageSize: this.pageSize, listGroups: true }
                }));
            }
            previousFranchiseListOptions = newFranchiseListOptions;
        });

        let previousFranchiseListOptionsForReview = {};
        const getFranchiseListForReviewSub = observableCombineLatest<any>(
            this.store.select(pageSelectors.getActiveTab),
            this.store.select(pageSelectors.getFranchiseForReviewAssetSortForSelectedFranchise),
            this.store.select(pageSelectors.getFranchiseForReviewAssetFilterForSelectedFranchise),
            this.store.select(fromRoot.getSidenavAssetProjectIds),
            this.store.select(fromRoot.getAssetAdding),
            this.store.select(pageSelectors.getSelectedFields),
            this.selectOptionsForReview$,
            this.store.select(fromRoot.getAssetLimit)
        ).pipe(filter(([activeTab, franchiseSort, _franchiseFilter, _projectIds, adding, f, _options, _limit]: [number, any, any, number[], ActionStatus, Field[], ColumnFilterOptions, number]) => {
            return !!f && !!f.length && activeTab === 1 && !!franchiseSort && adding === ActionStatus.Complete;
        })).subscribe(([activeTab, franchiseSort, franchiseFilter, projectIds, _adding, fields, selectOptions, assetLimit]: [number, any, any, number[], ActionStatus, Field[], ColumnFilterOptions, number]) => {
            const deptFilters = franchiseFilter ? franchiseFilter[STANDARD_FIELDS.department.canonical_name] : null;
            this.sidenav.filters[1] = setSidenavFilterDepartments(deptFilters, AssetIndexTypes.ForReview, this.currentView, this.sidenav.filters[1]);
            this.selectedDeptIdsList = this.sidenav.filters[1].selectedEntityIds;
            const { filters, syncWithRedux } = this.localStorageService.validateFiltersAndSelectOptions(franchiseFilter, selectOptions, this.forReviewStateKey);
            this.filtersForReview = { ...filters };
            this.selectedProjectIdsList = projectIds;
            fields = fields.map(field => ({ ...field, departmentFields: [] })) as Field[];
            this.optionsForReview = {
                sort: franchiseSort,
                filter: filters,
                projectIds,
                forReview: true,
                assetTypes: AssetTypes.Franchise,
                selectedFields: fields,
                limit: assetLimit,
                listGroups: true
            };

            if (syncWithRedux) {
                this.store.dispatch(new franchiseAssetIndexActions.FilterFranchiseForReviewListAction({ franchiseId: this.franchise.id, filter: this.filtersForReview }));
            }

            this.activeTab = this.tabs[activeTab];
            const newFranchiseListOptionsForReview = this.optionsForReview;
            if (reListingAssets(previousFranchiseListOptionsForReview, newFranchiseListOptionsForReview)) {
                const tableState = JSON.parse(localStorage.getItem(this.forReviewStateKey) ?? 'null');
                if (tableState) {
                    this.pageSizeForReview = (tableState.rows) ? tableState.rows : 50;
                    this.recordIndexForReview = 0;
                }

                let listOptions = {
                    ...this.optionsForReview,
                    recordIndex: this.recordIndexForReview,
                    pageSize: this.pageSizeForReview
                };

                if (newFranchiseListOptionsForReview.limit === -1 || this.allAssetsSelectedForReview) {
                    delete listOptions.recordIndex;
                    delete listOptions.pageSize;
                }

                if (this.allAssetsSelectedForReview) {
                    previousFranchiseListOptionsForReview = newFranchiseListOptionsForReview;
                    return;
                }

                this.forReviewNotObs = [];
                this.clearCacheForReview = true;  // ✅ Ensure cache cleared on relist
                this.store.dispatch(new assetActions.ListByFranchiseAction({
                    franchiseId: this.franchise.id,
                    options: listOptions
                }));
            }
            previousFranchiseListOptionsForReview = newFranchiseListOptionsForReview;
        });

        const fieldsSub = this.store.select(fieldSelectors.getAllWithFakeColumnsAndFranchiseRestricted).pipe(
            filter(f => !!f))
            .subscribe(fields => {
                this.fields = fields;
            });

        const getSelectedFieldsSub = this.store.select(pageSelectors.getSelectedFields).pipe(
            filter(f => !!f && !!f.length))
            .subscribe(fields => {
                this.selectedFields = fields.slice();
                this.selectedFieldOptions = fields.map(f => f.id);
                validateTableState(this.selectedFields, this.defaultStateKey);
                validateTableState(this.selectedFields, this.forReviewStateKey);
            });

        const getSidenavShowHideSub = this.sidenav.showHide();

        const getFieldsSub = this.store.select(pageSelectors.getFields).pipe(filter(fields => !!fields.length)).subscribe(fields => {
            this.allFieldOptions = fields.map(f => ({ label: f.canonical_name === 'franchise_id' ? 'Series/Feature' : f.name, value: f.id }));
            this.hasBarcodeField = fields.find((field) => field.type === FIELD_TYPES.BARCODE.type) ? true : false;
        });

        const getReviewAssetSortSub = this.store.select(pageSelectors.getFranchiseForReviewAssetSortForSelectedFranchise).subscribe((sort) => {
            if (sort.hasOwnProperty(this.franchise.id)) {
                // this.listForReviewOptions.sort = sort[this.franchise.id];
            }
        });

        const assetTypesSub = this.store.select(fromRoot.getAssetsShown).subscribe(type => {
            this.assetTypesShown = type;
            if (this.assetTypesShown) {
                this.store.dispatch(new assetActions.ListByFranchiseFiltersAction({ franchiseId: this.franchise.id, assetTypes: this.assetTypesShown }));
            }
        });

        const divisionsSub = this.store.select(divisionSelectors.getOwnedDivisionsWithWarehouses).subscribe(divs => this.moveToDivisions = divs);

        const pcSub = this.store.select(projectCurrencySelectors.getForSelectedProjectOnAsset).subscribe(pc => {
            if (pc.length > 0 && this.projectId) {
                const baseCurrency = pc.find(c => c.base === true);
                this.selectedProjectCurrencyId = this.storedSelectedPC[this.projectId] ? this.storedSelectedPC[this.projectId] : baseCurrency.id;
            }
        });

        const assetEntitiesSub = this.store.select(fromRoot.getAssetEntities).subscribe(assetEntities => {
            this.assetEntities = assetEntities;
        });

        const getForReviewAssetTotal = this.store.select(navSelectors.getNumOfForReviewAssets).subscribe(async numOfForReviewAssets => {
            if (numOfForReviewAssets > 0) {
                this.tabs[1].label = `For Review (${numOfForReviewAssets})`;
                this.forReviewNotification = true;
                const tempTab = this.activeTab
                this.activeTab = this.tabs[0];
                await new Promise(resolve => setTimeout(resolve, 1000));
                this.activeTab = tempTab
            } else {
                this.tabs[1].label = this.views.review;
                this.forReviewNotification = false;
            }
        });

        this.sub.add(getAssetsAndFiltersSub)
            .add(getSidenavShowHideSub)
            .add(getFieldsSub)
            .add(getReviewAssetSortSub)
            .add(assetTypesSub)
            .add(divisionsSub)
            .add(pcSub)
            .add(fieldsSub)
            .add(getSelectedFieldsSub)
            .add(getFranchiseListSub)
            .add(getFranchiseListForReviewSub)
            .add(assetEntitiesSub)
            .add(getForReviewAssetTotal);
    }

    getAssets(pageEvent: any) {
        let listOptions;
        if (pageEvent && pageEvent.recordIndex != null && pageEvent.pageSize != null) {
            const tableState = JSON.parse(localStorage.getItem(this.defaultStateKey));

            if (tableState) {
                if (pageEvent.pageSize != tableState.rows) {
                    this.clearCache = true;
                }
            }

            if (pageEvent.recordIndex == null) {
                pageEvent.recordIndex = 0;
            }

            if (this.currentView === AssetIndexTypes.ForReview) {
                listOptions = { ...this.optionsForReview, listGroups: true, projectIds: this.selectedProjectIdsList };
            } else {
                if (this.allAssetsSelected) {
                    listOptions = { ...this.options, listGroups: true, projectIds: this.selectedProjectIdsList };
                } else {
                    this.recordIndex = pageEvent.recordIndex;
                    this.pageSize = pageEvent.pageSize;
                    listOptions = { ...this.options, recordIndex: pageEvent.recordIndex, pageSize: pageEvent.pageSize, listGroups: true, projectIds: this.selectedProjectIdsList };
                }
            }
        }
        else {
            if (this.currentView === AssetIndexTypes.ForReview) {
                listOptions = { ...this.optionsForReview, projectIds: this.selectedProjectIdsList };
            } else {
                listOptions = { ...this.options, projectIds: this.selectedProjectIdsList };
            }
            this.recordIndex = null;
            this.pageSize = null;
            listOptions.limit = this.assetLimit;
            this.clearCache = true;
        }
        this.store.dispatch(new assetActions.ListByFranchiseAction({
            franchiseId: this.franchise.id,
            options: listOptions,
        }));
    }

    isAllAssetsSelcted(data: boolean) {
        this.allAssetsSelected = data;
    }

    isAllAssetsSelectedForReview(data: boolean) {
        this.allAssetsSelectedForReview = data;
    }

    clearAssetsCache(pageEvent: any) {
        this.clearCache = true;
        this.getAssets(pageEvent);
    }

    gotToAssetImport() {
        this.router.navigateByUrl(`/franchise/${this.franchise.id}/assets-import`);
    }

    gotToAssetImportSettings() {
        this.router.navigateByUrl(`/franchise/${this.franchise.id}/assets-import-history`);
    }

    exportAllCsvEvent() {
        const options = this.currentView === AssetIndexTypes.ForReview ? this.optionsForReview : this.options;
        let exportOptions = { ...options, currencyId: this.selectedProjectCurrencyId }
        this.store.dispatch(new layoutActions.InfoAction({ message: CSV_EXPORT_MESSAGE }));
        this.store.dispatch(new assetActions.ExportCsvFranchiseAction({ franchiseId: this.franchise.id, options: exportOptions }))
    }
}
